import {
  Package,
  ShoppingCart,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Users,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useSyncLogs } from '@/hooks/useSyncLogs';

function StatCard({
  title,
  value,
  icon: Icon,
  description,
  variant,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
  variant?: 'default' | 'warning';
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon
          className={cn(
            'h-4 w-4',
            variant === 'warning' ? 'text-warning' : 'text-muted-foreground',
          )}
        />
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'text-2xl font-bold',
            variant === 'warning' ? 'text-warning' : 'text-foreground',
          )}
        >
          {value}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function StatSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-3 w-32 mt-2" />
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentLogs, isLoading: logsLoading } = useSyncLogs({ per_page: 10 });

  return (
    <div className="h-full flex flex-col overflow-auto">
      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsLoading ? (
            <>
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
            </>
          ) : stats ? (
            <>
              <StatCard
                title="Total Products"
                value={stats.total_products}
                icon={Package}
                description={`${stats.active_products} active`}
              />
              <StatCard
                title="Total Orders"
                value={stats.total_orders}
                icon={ShoppingCart}
              />
              <StatCard
                title="Total Revenue"
                value={formatCurrency(stats.total_revenue)}
                icon={DollarSign}
              />
              <StatCard
                title="Missing Nursery Price"
                value={stats.missing_nursery_price ?? 0}
                icon={AlertTriangle}
                variant={(stats.missing_nursery_price ?? 0) > 0 ? 'warning' : 'default'}
                description="Products without nursery pricing"
              />
            </>
          ) : null}
        </div>

        {/* Second row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsLoading ? (
            <>
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
            </>
          ) : stats ? (
            <>
              <StatCard
                title="Total Variants"
                value={stats.total_variants}
                icon={Layers}
              />
              <StatCard
                title="Inventory Units"
                value={stats.total_inventory_units}
                icon={TrendingUp}
              />
              <StatCard
                title="Customers"
                value={stats.total_customers}
                icon={Users}
              />
              <StatCard
                title="Pending Edits"
                value={stats.products_with_edits}
                icon={RefreshCw}
                variant={stats.products_with_edits > 0 ? 'warning' : 'default'}
              />
            </>
          ) : null}
        </div>

        {/* Last sync */}
        {stats?.last_sync_at && (
          <p className="text-xs text-muted-foreground">
            Last sync: {formatDateTime(stats.last_sync_at)}
          </p>
        )}

        {/* Recent sync events */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recent Sync Events</CardTitle>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            ) : recentLogs?.data && recentLogs.data.length > 0 ? (
              <div className="space-y-2">
                {recentLogs.data.map((event) => (
                  <div
                    key={event.id}
                    className="flex flex-wrap items-center gap-2 border-b border-border py-2 last:border-0 sm:flex-nowrap sm:gap-3 sm:py-1.5"
                  >
                    <Badge
                      variant={event.direction === 'inbound' ? 'default' : 'secondary'}
                      className="text-[10px] w-16 justify-center"
                    >
                      {event.direction === 'inbound' ? 'IN' : 'OUT'}
                    </Badge>
                    <Badge
                      variant={
                        event.status === 'success'
                          ? 'success'
                          : event.status === 'failed'
                            ? 'destructive'
                            : 'warning'
                      }
                      className="text-[10px] w-14 justify-center"
                    >
                      {event.status}
                    </Badge>
                    <span className="min-w-0 flex-[1_1_100%] truncate text-sm text-foreground sm:flex-1">
                      {event.title}
                    </span>
                    <span className="text-xs text-muted-foreground sm:whitespace-nowrap">
                      {formatDateTime(event.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No sync events yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
